import { useRegistry } from '@/hooks/useRegistry'
import { useAuthors } from '@/hooks/useAuthors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dateToReadableDateTime } from '@/shared/datetime'
import { getAuthorBySub } from '@/lib/getAuthorBySub'
import { CAUSE_KEYS } from '@/defaults/causekeys'
import { HistoryEntry } from './HistoryEntry'
import { useVersionHistory } from './hooks/useVersionHistory'
import { useStatusHistory } from './hooks/useStatusHistory'
import type { StatusHistoryEntry } from './lib/buildStatusHistory'
import { statusLabel } from './lib/statusLabel'
import type { DocumentState } from '@/lib/getDocumentState'

const COLLAPSED_MAX = 4
const COLLAPSE_THRESHOLD = 5

/**
 * Timeline of a document. `versions` lists the latest document versions,
 * `statuses` lists every workflow status with who set it.
 */
export const DocumentHistory = ({
  uuid,
  currentVersion,
  onSelectVersion,
  selectedVersion,
  withStatusOnly = false,
  documentType,
  source = 'versions',
  collapsible = true,
  onSelectEntry
}: {
  uuid: string
  currentVersion?: bigint
  documentState?: DocumentState
  onSelectVersion?: (version: bigint) => void
  selectedVersion?: bigint
  withStatusOnly?: boolean
  documentType?: string
  source?: 'versions' | 'statuses'
  collapsible?: boolean
  onSelectEntry?: (entry: StatusHistoryEntry) => void
}) => {
  const { locale, timeZone } = useRegistry()
  const { t, i18n } = useTranslation()
  const authors = useAuthors()
  const [showAll, setShowAll] = useState(false)

  const { history, documents } = useVersionHistory({
    uuid,
    documentType,
    withStatusOnly,
    enabled: source === 'versions'
  })

  const { entries, error, isLoading } = useStatusHistory({
    uuid,
    documentType,
    enabled: source === 'statuses'
  })

  // Without this, loading and failed loads would look like an empty history
  let statusMessage: string | undefined
  if (source === 'statuses' && !entries?.length) {
    if (error) {
      statusMessage = t('errors:messages.failedToLoadHistory')
    } else if (isLoading) {
      statusMessage = t('common:misc.loading')
    }
  }

  const itemCount = (source === 'statuses' ? entries?.length : history?.length) ?? 0
  const isCollapsible = collapsible && itemCount > COLLAPSE_THRESHOLD
  const visibleCount = isCollapsible && !showAll ? COLLAPSED_MAX : itemCount

  const formatTime = (created: string) => {
    return dateToReadableDateTime(new Date(created), locale.code.short, timeZone)
  }

  const visibleEntries = source === 'statuses' ? entries?.slice(0, visibleCount) : undefined
  const visibleHistory = source === 'versions' ? history?.slice(0, visibleCount) : undefined

  return (
    <div className='flex flex-col gap-0 text-sm text-muted-foreground'>
      {statusMessage && <p className='text-xs'>{statusMessage}</p>}

      <div className='grid grid-cols-[1.5rem_auto_1fr]'>
        {visibleEntries?.map((entry, index) => (
          <div key={entry.key} className='grid grid-cols-subgrid col-span-3 group rounded'>
            <HistoryEntry
              version={entry.version > 0n ? entry.version : undefined}
              status={entry.status}
              label={statusLabel(entry.status, t, documentType)}
              cause={causeLabel(entry.cause)}
              publishVersion={entry.publishVersion}
              author={getAuthorBySub(authors, entry.creator)?.name || '???'}
              isLast={index === visibleEntries.length - 1}
              time={formatTime(entry.created)}
              onSelect={onSelectEntry ? () => onSelectEntry(entry) : undefined}
              selectOn='click'
            />
          </div>
        ))}

        {visibleHistory?.map((item, index) => {
          const title = documents?.find((doc) => doc.version === item.version)?.document?.title
          const isCurrent = item.version === currentVersion
          const status = documentType === 'core/factbox' && item.status === 'usable' && i18n.language !== 'nb'
            ? 'done'
            : item.status

          return (
            <div key={`${item.version}`} className='grid grid-cols-subgrid col-span-3 group rounded'>
              <HistoryEntry
                version={item.version}
                status={status}
                title={title}
                isLast={index === visibleHistory.length - 1}
                isCurrent={isCurrent}
                time={formatTime(item.created)}
                onSelect={onSelectVersion}
                selected={selectedVersion === item.version || (!selectedVersion && isCurrent)}
              />
            </div>
          )
        })}
      </div>

      {isCollapsible && (
        <button
          type='button'
          onMouseDownCapture={(event) => {
            event.preventDefault()
            setShowAll((prev) => !prev)
          }}
          className='mt-1 text-xs text-muted-foreground/70 hover:text-foreground transition-colors text-left cursor-pointer'
        >
          {showAll
            ? t('wires:history.showLess')
            : t('wires:history.showAll', { count: itemCount })}
        </button>
      )}
    </div>
  )
}


function causeLabel(cause?: string): string | undefined {
  if (!cause || !(cause in CAUSE_KEYS)) {
    return undefined
  }

  return CAUSE_KEYS[cause as keyof typeof CAUSE_KEYS].short
}
